import * as React from 'react';

// =============================================================================
// use-grid-keyboard-nav.ts — Navigation clavier inter-cellules de la grille SAISIE 82×4.
// =============================================================================
// a11y WCAG 2.1.1 (Keyboard) : la grille profil×niveau est traversable AU CLAVIER par les
// flèches (↑↓←→) en plus du Tab natif. Pattern : DÉLÉGATION d'un unique listener keydown au
// conteneur <table> (PAS un listener par cellule) — clé de la fluidité 82×4 : aucun coût par
// cellule, l'édition perçue reste < 150 ms (ENF-01 / ST-NFR-01), l'onChange de l'input est
// INTACT (la frappe ne passe jamais par ce handler).
//
// Le listener est attaché en NATIF (addEventListener via ref callback) plutôt qu'en prop JSX
// `onKeyDown` : (1) le <table> garde son rôle implicite `table` (zéro régression des requêtes
// getByRole('table') du reste de l'UI) ; (2) on n'attache pas un handler clavier à un élément
// non-interactif au sens JSX (jsx-a11y), la cible clavier réelle restant l'<input> de cellule.
//
// Repérage des cellules : chaque <input> éditable porte `data-grid-row` / `data-grid-col`
// (indices 0-based). Le déplacement BORNE aux limites de la grille (pas de débordement). Les
// flèches ←/→ ne volent le focus QUE lorsque le caret est au bord du texte (début pour ←, fin
// pour →) : l'édition intra-cellule reste naturelle. ↑/↓ traversent toujours les lignes.
// Entrée = descendre d'une ligne (convention tableur, commit via onBlur). Échap = sortir de
// l'édition (blur ⇒ flush autosave existant, logique métier INCHANGÉE).

/** Attribut d'indexation de ligne porté par l'input d'une cellule de grille. */
export const GRID_ROW_ATTR = 'data-grid-row';
/** Attribut d'indexation de colonne porté par l'input d'une cellule de grille. */
export const GRID_COL_ATTR = 'data-grid-col';

/** Sélecteur d'un input de cellule à des coordonnées données. */
function cellSelector(row: number, col: number): string {
  return `input[${GRID_ROW_ATTR}="${row}"][${GRID_COL_ATTR}="${col}"]`;
}

/** Lit les coordonnées (row, col) d'un élément input de cellule, ou `null` si absent/non valide. */
function readCoords(el: Element | null): { row: number; col: number } | null {
  if (el === null) {
    return null;
  }
  const rawRow = el.getAttribute(GRID_ROW_ATTR);
  const rawCol = el.getAttribute(GRID_COL_ATTR);
  if (rawRow === null || rawCol === null) {
    return null;
  }
  const row = Number.parseInt(rawRow, 10);
  const col = Number.parseInt(rawCol, 10);
  if (Number.isNaN(row) || Number.isNaN(col)) {
    return null;
  }
  return { row, col };
}

/** Le caret de l'input est-il au tout début du texte (autorise ← à changer de cellule) ? */
function caretAtStart(input: HTMLInputElement): boolean {
  return input.selectionStart === 0 && input.selectionEnd === 0;
}

/** Le caret de l'input est-il à la toute fin du texte (autorise → à changer de cellule) ? */
function caretAtEnd(input: HTMLInputElement): boolean {
  const end = input.value.length;
  return input.selectionStart === end && input.selectionEnd === end;
}

/**
 * Traite un keydown de navigation inter-cellules. `container` = la grille (<table>) sur laquelle
 * sont délégués les déplacements. Ne touche NI la valeur NI l'autosave (le blur déclenché par
 * Échap réutilise la logique existante).
 */
function handleGridKeyDown(event: KeyboardEvent, container: HTMLElement): void {
  const { key } = event;
  // On ne pilote que les touches de navigation ; tout le reste (frappe, Tab) suit le natif.
  if (
    key !== 'ArrowUp' &&
    key !== 'ArrowDown' &&
    key !== 'ArrowLeft' &&
    key !== 'ArrowRight' &&
    key !== 'Enter' &&
    key !== 'Escape'
  ) {
    return;
  }

  const active = event.target as Element | null;
  if (active === null || active.tagName !== 'INPUT') {
    return;
  }
  const input = active as HTMLInputElement;
  const coords = readCoords(input);
  if (coords === null) {
    // L'input ciblé n'est pas une cellule de grille (sécurité) : ne rien intercepter.
    return;
  }

  // Échap : sortir de l'édition (blur ⇒ flush autosave existant). Aucun déplacement.
  if (key === 'Escape') {
    input.blur();
    return;
  }

  let nextRow = coords.row;
  let nextCol = coords.col;

  switch (key) {
    case 'ArrowUp':
      nextRow = coords.row - 1;
      break;
    case 'ArrowDown':
    case 'Enter':
      nextRow = coords.row + 1;
      break;
    case 'ArrowLeft':
      // ← ne change de cellule QUE si le caret est au début (sinon édition de texte native).
      if (!caretAtStart(input)) {
        return;
      }
      nextCol = coords.col - 1;
      break;
    case 'ArrowRight':
      // → ne change de cellule QUE si le caret est à la fin (sinon édition de texte native).
      if (!caretAtEnd(input)) {
        return;
      }
      nextCol = coords.col + 1;
      break;
    default:
      return;
  }

  // Borne : pas de débordement hors grille (aucune cible voisine ⇒ comportement « buté »).
  const target = container.querySelector<HTMLInputElement>(cellSelector(nextRow, nextCol));
  if (target === null) {
    // Bord de grille : on absorbe les flèches (pas de défilement de page / saut de caret) mais on
    // ne déplace rien. Entrée reste laissée au natif (pas de blocage de soumission éventuelle).
    if (key !== 'Enter') {
      event.preventDefault();
    }
    return;
  }

  // Déplacement effectif : empêcher le défilement de page / déplacement de caret par défaut.
  event.preventDefault();
  target.focus();
  // Place le caret en fin de la cellule cible (saisie immédiatement poursuivable).
  const len = target.value.length;
  try {
    target.setSelectionRange(len, len);
  } catch {
    // Certains types d'input n'autorisent pas setSelectionRange : focus seul suffit.
  }
}

/**
 * Navigation clavier inter-cellules pour la grille de saisie (WCAG 2.1.1). Retourne un REF
 * CALLBACK à poser sur le `<table>` : il attache/détache un listener `keydown` natif délégué.
 * Le natif (vs prop JSX `onKeyDown`) préserve le rôle implicite `table` et évite d'assigner un
 * handler clavier à un élément non-interactif (jsx-a11y) — la cible clavier réelle est l'input.
 */
export function useGridKeyboardNav(): (node: HTMLTableElement | null) => void {
  const cleanupRef = React.useRef<(() => void) | null>(null);

  return React.useCallback((node: HTMLTableElement | null): void => {
    // Détache le listener du nœud précédent (remount / changement de ref).
    if (cleanupRef.current !== null) {
      cleanupRef.current();
      cleanupRef.current = null;
    }
    if (node === null) {
      return;
    }
    const listener = (event: KeyboardEvent): void => {
      handleGridKeyDown(event, node);
    };
    node.addEventListener('keydown', listener);
    cleanupRef.current = (): void => {
      node.removeEventListener('keydown', listener);
    };
  }, []);
}
